<template>
  <a-row
    class="menu"
    :class="{ menu__attachments: existFile }"
    type="flex"
    justify="space-between"
    align="top"
  >
    <a-row type="flex">
      <!--Emojis-->
      <div
        class="menu__option"
        :class="{
          'menu__option--disabled': props.disabledEmoji || props.disabledAll,
        }"
      >
        <emoji-picker
          :icon-styles="{
            fontSize: '16px',
            color: props.disabledEmoji || props.disabledAll ? '#BFBFBF' : 'rgba(0, 0, 0, 0.65)',
          }"
          :show-tooltip="true"
          :icon-from-ant="true"
          @onChangeEmoji="(emoji) => $emit('onChangeEmoji', emoji)"
          :disabled="props.disabledEmoji || props.disabledAll"
        />
      </div>
      <!-- Adjuntar documentos -->
      <a-upload
        v-if="showMedia"
        v-model:file-list="fileAttachments"
        class="menu__upload"
        :openFileDialogOnClick="!existFile && !props.disabledMedia"
        :beforeUpload="(file) => beforeUploadAttachment(MultimediaKeyOption.Document, file)"
        :customRequest="selfUpload"
        @preview="handleShowPreview"
        @remove="handleRemoveFile"
      >
        <a-tooltip :overlayInnerStyle="{ textAlign: 'center' }">
          <template v-if="!props.disabledMedia" #title>
            <template v-if="existFile">1 archivo máximo</template>
            <template v-else>
              {{ attachDocument.title }} <br />
              ({{ attachDocument.max_size_title }})
            </template>
          </template>
          <div
            class="menu__option"
            :class="{ 'menu__option--disabled': existFile || props.disabledMedia }"
          >
            <custom-icon class="option__icon">
              <img :src="paperClip2Image" v-svg-inline alt="icono de adjuntar archivo" />
            </custom-icon>
          </div>
        </a-tooltip>
      </a-upload>
      <!-- Adjuntar Media -->
      <a-upload
        v-if="showMedia"
        v-model:file-list="fileAttachments"
        class="menu__upload"
        :openFileDialogOnClick="!existFile && !props.disabledMedia"
        :beforeUpload="(file) => beforeUploadAttachment(MultimediaKeyOption.Media, file)"
        :customRequest="selfUpload"
        @preview="handleShowPreview"
        @remove="handleRemoveFile"
      >
        <a-tooltip :overlayInnerStyle="{ textAlign: 'center' }">
          <template v-if="!props.disabledMedia" #title>
            <template v-if="existFile">1 archivo máximo</template>
            <template v-else>
              {{ attachMedia.title }} <br />
              ({{ attachMedia.max_size_title }})
            </template>
          </template>
          <div
            class="menu__option"
            :class="{ 'menu__option--disabled': existFile || props.disabledMedia }"
          >
            <custom-icon class="option__icon">
              <img :src="attachImage" v-svg-inline alt="icono de adjuntar media" />
            </custom-icon>
          </div>
        </a-tooltip>
      </a-upload>

      <!--Listado de variables-->
      <a-tooltip>
        <template v-if="props.loadingVars" #title>Cargando...</template>
        <template v-else-if="props.disabledVars || props.disabledAll" #title>
          Variables: Para utilizar esta opción, debes haber guardado una respuesta con una variable,
          previamente en este chatbot.
        </template>
        <div
          class="menu__option"
          :class="{
            'menu__option--disabled': props.disabledVars || props.loadingVars || props.disabledAll,
          }"
          @click="handleOpenVars"
        >
          <loading-outlined v-if="props.loadingVars" class="option__icon" />
          <number-outlined v-else class="option__icon" />
        </div>
      </a-tooltip>
    </a-row>
    <!--Esperar respuesta-->
    <a-row v-if="props.showWait" type="flex" align="middle">
      <a-checkbox
        v-model:checked="checkedWait"
        :disabled="props.disabledWaitAnswer || props.disabledAll"
        @change="handleChangeWait"
      >
        Esperar una respuesta
      </a-checkbox>
      <a-tooltip>
        <template #title>
          Tu chatbot esperará una respuesta de tu cliente para continuar.
        </template>
        <InfoCircleOutlined
          class="radio-message__icon"
          style="height: fit-content; margin-top: 2px"
        />
      </a-tooltip>
    </a-row>
    <ModalPreviewMedia v-bind="modalPreviewMedia" @onClose="handleCloseModalPreview" />
  </a-row>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch } from "vue";
import EmojiPicker from "@/app/shared/components/pickers/EmojiPicker.vue";
import ModalPreviewMedia from "@/app/chatbots/components/modals/ModalPreviewMedia.vue";
import { NumberOutlined, InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons-vue";
import CustomIcon from "@/app/shared/components/icons/CustomIcon.vue";
import { message, Upload } from "ant-design-vue";
import {
  MULTIMEDIA_OPTIONS,
  MULTIMEDIA_TYPES,
  formattedMedia,
} from "@/app/chatbots/utils/multimedia";
import { MultimediaKeyOption, Multimedia } from "@/@types/chatbots/multimedia";
import { MediaNode } from "@/@types/chatbots";
import { NodeType } from "@/app/chatbots/utils/enums";
import type { UploadProps, UploadFile } from "ant-design-vue";
// import verifyExistCatcher from '@/app/chatbots/mixins/actionsToNodes'

import paperClip2Image from "@/app/shared/assets/anron/paperclip-2.svg?url";
import attachImage from "@/app/shared/assets/anron/image-3.svg?url";

const props = withDefaults(
  defineProps<{
    disabledWaitAnswer?: boolean;
    valueCheck?: boolean;
    showWait?: boolean;
    disabledEmoji?: boolean;
    disabledVars?: boolean;
    loadingVars?: boolean;
    keyMedia?: string;
    defaultMedia?: MediaNode;
    disabledMedia?: boolean;
    showMedia?: boolean;
    disabledAll?: boolean;
  }>(),
  {
    showWait: true,
    keyMedia: "media",
    showMedia: true,
  }
);

const emits = defineEmits(["onChangeWait", "onChangeEmoji", "onOpenVars", "onChangeFile"]);

const checkedWait = ref(props.valueCheck || false);
const defaultMediaData = computed<UploadFile | undefined>(() => {
  if (!props.defaultMedia || !props.defaultMedia[props.keyMedia as keyof typeof props.defaultMedia])
    return;
  return formattedMedia(props.defaultMedia, props.keyMedia);
});

const fileAttachments = defaultMediaData.value
  ? ref<UploadProps["fileList"]>([defaultMediaData.value])
  : ref<UploadProps["fileList"]>([]);
const multimediaType = ref();
const modalPreviewMedia = reactive({
  visible: false,
  title: "",
  type: "",
  url: "",
});

watch(
  () => props.valueCheck,
  (value) => (checkedWait.value = value)
);

watch(
  () => props.defaultMedia,
  () => {
    fileAttachments.value = defaultMediaData.value ? [defaultMediaData.value] : [];
  }
);

const attachDocument = computed(() => {
  return MULTIMEDIA_OPTIONS.file;
});

const attachMedia = computed(() => {
  return MULTIMEDIA_OPTIONS.media;
});

const existFile = computed(() => {
  return fileAttachments.value && !!fileAttachments.value.length;
});

/**
 * Cambia el valor de wait
 */
const handleChangeWait = () => {
  emits("onChangeWait", checkedWait.value);
};

const handleOpenVars = () => {
  if (props.disabledVars || props.loadingVars) return;
  emits("onOpenVars");
};

const beforeUploadAttachment = (type: MultimediaKeyOption, file: File) => {
  const fileType = file.type;
  const multimediaOptions = MULTIMEDIA_OPTIONS[type].multimedia;
  const multimediaTypes = multimediaOptions.map((media) => MULTIMEDIA_TYPES[media]);
  multimediaType.value = multimediaTypes.find((mutimediaType) =>
    mutimediaType.formats.accept.includes(fileType)
  );

  if (!multimediaType.value) {
    message.error("Archivo no permitido");
    return false || Upload.LIST_IGNORE;
  }
  const isValidSize = file.size / 1024 / 1024 <= multimediaType.value.max_size;
  if (!isValidSize) {
    message.error(`El tamaño máximo es ${multimediaType.value.max_size}MB`);
    return false || Upload.LIST_IGNORE;
  }

  return true;
};
/**
 * Subida de archivo
 * @param {Object} file
 */
const selfUpload = ({
  file,
  onSuccess,
}: {
  file: File;
  onSuccess: (file: string | ArrayBuffer) => void;
}) => {
  const keyMessage = "message-loading";
  message.loading({ key: keyMessage, content: "Subiendo..." });
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      emits("onChangeFile", {
        media: { [props.keyMedia]: file },
        type: multimediaType.value.type_in_node,
      });
      if (fileReader && fileReader.result) onSuccess(fileReader.result);
      resolve(fileReader.result);
      message.success({ key: keyMessage, content: "Subido" });
    };
  });
};

const handleRemoveFile = () => {
  console.log("remove file");
  emits("onChangeFile", {
    media: { [props.keyMedia]: "" },
    type: NodeType.Message, // solo mensaje
  });
};

const handleShowPreview = (file: UploadFile) => {
  if (!file) return;
  // si es de tipo archivo descargará
  if (file.type === Multimedia.Document) return window.open(file.url);
  // si es video o imagen, se mostrará el modal
  modalPreviewMedia.type = file.type || "";
  modalPreviewMedia.title = file.name;
  modalPreviewMedia.url = file.url || "";
  modalPreviewMedia.visible = true;
};
const handleCloseModalPreview = () => {
  modalPreviewMedia.visible = false;
  modalPreviewMedia.title = "";
  modalPreviewMedia.type = "";
  modalPreviewMedia.url = "";
};

const resetChanges = () => {
  console.log("reset");
  fileAttachments.value = defaultMediaData.value ? [defaultMediaData.value] : [];
};

defineExpose({ resetChanges });
</script>

<style lang="sass" scoped>
$size_option: 28px

=flex($direction, $content, $align)
  display: flex
  flex-direction: $direction
  justify-content: $content
  align-items: $align
.menu
  margin-top: 8px
  margin-bottom: 12px

.menu
  &__option
    padding: 6px
    background-color: $gray-3
    width: $size_option
    margin-right: 4px
    border-radius: 4px
    height: $size_option
    @include flex(row, center, center)
    cursor: pointer
    color: $text-secondary
    &--disabled
      color: $gray-6
      cursor: not-allowed
      pointer-events: none
  &__attachments
    height: 58px
.option__icon
  font-size: 16px
.radio-message
  &__icon
    font-size: 14px
</style>
<style lang="sass">
.menu__upload .ant-upload-list
  position: absolute
  width: 100%
  left: 0px
</style>
